/**
 * Quantity takeoff (eval/TAKEOFF_PLAN.md): wire types, the REST client, page-space
 * geometry and mark numbering. Geometry mirrors backend/apps/takeoff/geometry.py
 * and numbering mirrors quantities.compute (`seq` = order by created_at, id per
 * target per sheet), so every client numbers simultaneous clicks the same way.
 */
import type { ApiClient } from './api';
import type { AnnotationDTO, PointDTO } from './types';

// ---------------------------------------------------------------------------
// Wire types
// ---------------------------------------------------------------------------

export type Uom = 'EACH' | 'LF' | 'M' | 'SF' | 'M2' | 'SPOOL' | 'LOT';
export type TakeoffCategory = 'luminaire' | 'driver' | 'control' | 'network' | 'av' | 'cable' | 'other';
export type TakeoffSymbol = 'circle' | 'square' | 'triangle' | 'diamond' | 'hexagon' | 'star';
export type ItemSource = 'schedule' | 'inferred' | 'design' | 'workbook';
export type MeasureKind = 'count' | 'linear' | 'area';
export type MaskKind = 'legend' | 'title_block' | 'notes' | 'other';

/** `Annotation.takeoff`: exactly one of item_id / assembly_id plus per-mark overrides. */
export interface TakeoffLink {
  item_id?: string;
  assembly_id?: string;
  area_id?: string;
  zone?: string;
  scope?: string;
  unit_type_id?: string;
  runs?: number;
  allowance?: number;
  source?: string;
}

interface Versioned {
  id: string;
  version: number;
  sort_order: number;
}

export interface TakeoffItemDTO extends Versioned {
  /** Against a linked Estimating Workbook: '' none · pending (row being added) · linked · removed (gone from PROJECT BOM). */
  workbook_state?: '' | 'pending' | 'linked' | 'removed';
  code: string;
  name: string;
  description: string;
  make: string;
  model: string;
  part_no: string;
  wattage: string;
  accessories: string;
  notes: string;
  uom: Uom;
  category: TakeoffCategory;
  color: string;
  symbol: TakeoffSymbol;
  zone: string;
  /** Bid scope for every placement of this type ('' = inherit: zone / frame / BASE BID). Board-owned. */
  scope?: string;
  spec_reference: string;
  source: ItemSource;
  verify: boolean;
  expected_total: number | null;
  waste_pct: number;
  run_allowance: number;
  /** "Count along the run": remembered length of one fixture (board-owned quantity rule). */
  run_unit_length?: number | null;
  run_unit?: '' | 'm' | 'ft';
  unit_cost: string | null;
  /** Design-estimate pricing (apps.takeoff.pricing). */
  price_source?: PriceSource;
  confidence?: Confidence;
  labour_hours?: number | null;
  labour_pct?: number | null;
  favorite_slot: number | null;
}

export type PriceSource = '' | 'list' | 'quote' | 'historical' | 'guess';
export type Confidence = '' | 'high' | 'medium' | 'low';

export interface ComponentDTO {
  id?: string;
  item: string | null;
  child_assembly: string | null;
  qty_per_host: number;
  qty_label: string;
  label?: string;
  notes?: string;
  sort_order?: number;
}

export interface TakeoffAssemblyDTO extends Versioned {
  name: string;
  code: string;
  host_label: string;
  host_word: string;
  zone: string;
  /** Bid scope for this assembly's package lines ('' = inherit). */
  scope?: string;
  zone_label: string;
  description: string;
  notes: string;
  color: string;
  symbol: TakeoffSymbol;
  measure: MeasureKind;
  expected_count: number | null;
  spec_reference: string;
  favorite_slot: number | null;
  components: ComponentDTO[];
}

export interface TakeoffSheetDTO {
  id: string;
  page_index: number;
  sheet_number: string;
  title: string;
  label: string;
  points_per_unit: number | null;
  scale_unit: 'ft' | 'm';
  scale_label: string;
  scale_method: string;
  version: number;
}

export interface TakeoffAreaDTO extends Versioned {
  name: string;
  scope: string;
  page_index: number | null;
  vertices: PointDTO[] | null;
  color: string;
  /** Boards: the frame this area is (Excalidraw element id), its typical-unit multiplier and drawing scale. */
  frame_id?: string;
  multiplier?: number;
  units_per_px?: number | null;
  scale_unit?: 'ft' | 'm';
}

export interface UnitRegionDTO {
  id?: string;
  page_index: number;
  vertices: PointDTO[];
}

export interface UnitTypeDTO extends Versioned {
  name: string;
  multiplier: number;
  color: string;
  regions: UnitRegionDTO[];
}

export interface MaskDTO extends Versioned {
  page_index: number;
  kind: MaskKind;
  vertices: PointDTO[];
  label: string;
  copied_from: string | null;
}

export interface ScalePreset {
  label: string;
  points_per_unit: number;
  unit: 'ft' | 'm';
}

export interface PricingSettings {
  currency: string;
  labour_rate: number;
  labour_pct: number;
  tax_pct: number;
  freight_pct: number;
  contingency_pct: number;
  markup_pct: number;
  rom_low_pct: number;
  rom_high_pct: number;
  /** How runs / areas read: 24'-6" (ftin), 24.5 ft (ft) or 7.47 m (m). */
  measure_display?: 'ftin' | 'ft' | 'm';
}

export interface TakeoffStateDTO {
  id: string;
  document_id: string | null;
  board_id?: string | null;
  default_scope: string;
  pricing?: PricingSettings;
  version: number;
  items: TakeoffItemDTO[];
  assemblies: TakeoffAssemblyDTO[];
  sheets: TakeoffSheetDTO[];
  areas: TakeoffAreaDTO[];
  unit_types: UnitTypeDTO[];
  masks: MaskDTO[];
  scale_presets: ScalePreset[];
}

export type Check = 'ok' | 'mismatch' | 'none';

export interface QuantityItem {
  item_id: string;
  type: string;
  name: string;
  description: string;
  uom: Uom;
  total: number;
  expected_total: number | null;
  check: Check;
  verify: boolean;
  source: ItemSource;
  spec_reference: string;
}

export interface QuantityHost {
  assembly_id: string;
  name: string;
  zone: string;
  marks: number;
  count: number;
  expected_count: number | null;
  check: Check;
  by_area: { area: string; count: number }[];
  package: { item_id: string | null; type: string; qty_per_host: number; label: string }[];
}

export interface QuantityRow {
  scope: string;
  area: string;
  zone: string;
  type: string;
  item_id: string;
  qty: number;
  formula: string;
}

export interface QuantitiesDTO {
  version: number;
  items: QuantityItem[];
  hosts: QuantityHost[];
  rows: QuantityRow[];
  tuples: Record<string, string | number>[];
  by_sheet: { page_index: number; sheet: string; item_id: string; type: string; qty: number }[];
  by_unit: { unit: string; multiplier: number; item_id: string; type: string; per_unit: number; qty: number }[];
  measurements: {
    annotation_id: string;
    type: MeasureKind;
    page_index: number;
    sheet: string;
    seq: number;
    target_kind: 'item' | 'assembly';
    target_id: string;
    target_code: string;
    target_name: string;
    area: string;
    zone?: string;
    scope: string;
    unit: string;
    quantity: number | null;
  }[];
  checks: { kind: 'item' | 'host'; id: string; label: string; actual: number; expected: number | null }[];
  warnings: { annotation_id: string; page_index: number; reason: 'no_scale' | 'unassigned' | 'cycle' | 'spans_images' }[];
  /** Null when a workbook is linked: pricing lives there (`pricing_in_workbook`). */
  budget?: BudgetDTO | null;
  pricing_in_workbook?: boolean;
  /** Boards: every frame (= area) with its estimate settings. */
  board_areas?: BoardAreaDTO[];
}

export interface BudgetRoll {
  material: number;
  labour: number;
  tax: number;
  freight: number;
  subtotal: number;
  contingency: number;
  markup: number;
  total: number;
  low: number;
  high: number;
}

export interface BudgetLine {
  item_id: string;
  type: string;
  description: string;
  category: TakeoffCategory;
  uom: Uom;
  qty: number;
  unit_cost: number | null;
  material: number | null;
  labour: number;
  labour_hours: number | null;
  labour_basis: string;
  price_source: PriceSource;
  confidence: Confidence;
  priced: boolean;
  specified: boolean;
}

export interface BudgetDTO {
  currency: string;
  settings: PricingSettings;
  order_of_operations: string[];
  lines: BudgetLine[];
  totals: BudgetRoll & { labour_hours: number };
  categories: (BudgetRoll & { category: TakeoffCategory })[];
  areas: (BudgetRoll & { area: string })[];
  confidence: { confidence: string; material: number; share: number }[];
  tbd: { unpriced: string[]; unspecified: string[] };
}

export interface BoardAreaDTO {
  frame_id: string;
  name: string;
  multiplier: number;
  scope: string;
  area_id: string | null;
  units_per_px: number | null;
  scale_unit: 'ft' | 'm';
}

export interface TakeoffChange {
  kind: 'takeoff' | 'item' | 'assembly' | 'area' | 'unit_type' | 'mask' | 'sheet';
  action: 'created' | 'updated' | 'deleted';
  data: Record<string, unknown>;
  version: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const UOMS: readonly Uom[] = ['EACH', 'LF', 'M', 'SF', 'M2', 'SPOOL', 'LOT'];
export const CATEGORIES: readonly TakeoffCategory[] = ['luminaire', 'driver', 'control', 'network', 'av', 'cable', 'other'];
export const SYMBOLS: readonly TakeoffSymbol[] = ['circle', 'square', 'triangle', 'diamond', 'hexagon', 'star'];
/** Eos Estimating Workbook TYPE prefixes (REFERENCE TABLES G:I). */
export const TYPE_PREFIXES = ['AU', 'CA', 'CP', 'EC', 'FX', 'IN', 'L', 'LA', 'LD', 'LX', 'MB', 'NE', 'RB', 'SW', 'TR', 'UP', 'VD', 'WS'] as const;
export const MASK_KINDS: readonly MaskKind[] = ['legend', 'title_block', 'notes', 'other'];
export const TAKEOFF_COLORS = ['#E53935', '#1E88E5', '#43A047', '#FB8C00', '#8E24AA', '#00ACC1', '#6D4C41', '#F4511E', '#3949AB', '#7CB342'];
export const MEASURE_TYPES: ReadonlySet<string> = new Set(['count', 'linear', 'area']);
export const MARK_SIZE = 14;

/** Next free TYPE code for a prefix (LD01, LD02 …). */
export function nextTypeCode(prefix: string, items: readonly Pick<TakeoffItemDTO, 'code'>[]): string {
  const p = prefix.toUpperCase();
  let n = 1;
  const used = new Set(items.map((i) => i.code.toUpperCase()));
  while (used.has(`${p}${String(n).padStart(2, '0')}`)) n++;
  return `${p}${String(n).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// Geometry (page space: PDF points, origin bottom-left)
// ---------------------------------------------------------------------------

type P = { x: number; y: number };

export function polylineLength(pts: readonly P[]): number {
  let s = 0;
  for (let i = 0; i + 1 < pts.length; i++) s += Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
  return s;
}

export function polylineMidpoint(pts: readonly P[]): P {
  if (!pts.length) return { x: 0, y: 0 };
  const total = polylineLength(pts);
  if (total <= 0) return pts[0];
  const half = total / 2;
  let run = 0;
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i], b = pts[i + 1];
    const seg = Math.hypot(b.x - a.x, b.y - a.y);
    if (run + seg >= half) {
      const t = seg ? (half - run) / seg : 0;
      return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    }
    run += seg;
  }
  return pts[pts.length - 1];
}

export function polygonArea(pts: readonly P[]): number {
  if (pts.length < 3) return 0;
  let s = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    s += a.x * b.y - b.x * a.y;
  }
  return Math.abs(s) / 2;
}

export function polygonCentroid(pts: readonly P[]): P {
  const n = pts.length;
  if (!n) return { x: 0, y: 0 };
  let a = 0, cx = 0, cy = 0;
  for (let i = 0; i < n; i++) {
    const p = pts[i], q = pts[(i + 1) % n];
    const cross = p.x * q.y - q.x * p.y;
    a += cross;
    cx += (p.x + q.x) * cross;
    cy += (p.y + q.y) * cross;
  }
  if (Math.abs(a) < 1e-9) return { x: pts.reduce((s, p) => s + p.x, 0) / n, y: pts.reduce((s, p) => s + p.y, 0) / n };
  a *= 0.5;
  return { x: cx / (6 * a), y: cy / (6 * a) };
}

export function pointInPolygon(x: number, y: number, pts: readonly P[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i].x, yi = pts[i].y, xj = pts[j].x, yj = pts[j].y;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi || 1e-12) + xi) inside = !inside;
  }
  return inside;
}

/** Where a measurement "is" (area/unit assignment): count = centre, linear = half-way, area = centroid. */
export function anchorOf(a: Pick<AnnotationDTO, 'type' | 'bounds' | 'vertices'>): P {
  const v = a.vertices ?? [];
  if ((a.type as string) === 'linear' && v.length >= 2) return polylineMidpoint(v);
  if (a.type === 'area' && v.length >= 3) return polygonCentroid(v);
  return { x: a.bounds.x + a.bounds.width / 2, y: a.bounds.y + a.bounds.height / 2 };
}

const FT_PER_M = 1 / 0.3048;

/** Page points → real length in `uom` (LF feet, M metres; else the sheet's unit). Null without a scale. */
export function toRealLength(pts: number, sheet: Pick<TakeoffSheetDTO, 'points_per_unit' | 'scale_unit'> | undefined | null, uom: Uom | 'ft' | 'm' = 'LF'): number | null {
  if (!sheet?.points_per_unit) return null;
  const real = pts / sheet.points_per_unit;
  if ((uom === 'M' || uom === 'm') && sheet.scale_unit === 'ft') return real / FT_PER_M;
  if ((uom === 'LF' || uom === 'ft') && sheet.scale_unit === 'm') return real * FT_PER_M;
  return real;
}

export function toRealArea(ptsSq: number, sheet: Pick<TakeoffSheetDTO, 'points_per_unit' | 'scale_unit'> | undefined | null, uom: Uom = 'SF'): number | null {
  if (!sheet?.points_per_unit) return null;
  const real = ptsSq / sheet.points_per_unit ** 2;
  if (uom === 'M2' && sheet.scale_unit === 'ft') return real / FT_PER_M ** 2;
  if (uom === 'SF' && sheet.scale_unit === 'm') return real * FT_PER_M ** 2;
  return real;
}

/** Feet-and-inches for a real length in feet (20.5 → 20'-6"). */
export function feetInches(ft: number): string {
  const neg = ft < 0;
  let f = Math.floor(Math.abs(ft));
  let i = Math.round((Math.abs(ft) - f) * 12);
  if (i === 12) {
    f += 1;
    i = 0;
  }
  return `${neg ? '-' : ''}${f}'-${i}"`;
}

export function fmtQty(x: number | null | undefined): string {
  if (x == null || Number.isNaN(x)) return '—';
  return Math.abs(x - Math.round(x)) < 1e-6 ? String(Math.round(x)) : x.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

/** Parse a real length like `20'-6"`, `20.5`, `6.2 m`, `20 ft`. Returns the number in `unit`. */
export function parseLength(input: string, unit: 'ft' | 'm' = 'ft'): number | null {
  const s = input.trim().toLowerCase();
  const fi = s.match(/^(\d+(?:\.\d+)?)\s*'\s*-?\s*(\d+(?:\.\d+)?)?\s*(?:"|'')?$/);
  if (fi) {
    const ft = Number(fi[1]) + Number(fi[2] ?? 0) / 12;
    return unit === 'ft' ? ft : ft / FT_PER_M;
  }
  const m = s.match(/^(\d+(?:\.\d+)?)\s*(ft|feet|'|m|meters?|metres?|mm)?$/);
  if (!m) return null;
  const v = Number(m[1]);
  const u = m[2] ?? unit;
  const inFt = u === 'm' || u.startsWith('met') ? v * FT_PER_M : u === 'mm' ? (v / 1000) * FT_PER_M : v;
  return unit === 'ft' ? inFt : inFt / FT_PER_M;
}

// ---------------------------------------------------------------------------
// Marks
// ---------------------------------------------------------------------------

export function isMeasurement(a: Pick<AnnotationDTO, 'type'> & { takeoff?: TakeoffLink | null }): boolean {
  return !!a.takeoff && MEASURE_TYPES.has(a.type as string);
}

export function targetOf(a: { takeoff?: TakeoffLink | null }): { kind: 'item' | 'assembly'; id: string } | null {
  const t = a.takeoff;
  if (!t) return null;
  if (t.assembly_id) return { kind: 'assembly', id: t.assembly_id };
  if (t.item_id) return { kind: 'item', id: t.item_id };
  return null;
}

/**
 * Mark numbers: per target per sheet, ordered by (created_at, id). Optimistic
 * rows without a server time sort last in their group. Deterministic for every
 * client, so two users counting at once never see colliding numbers.
 */
export function markSequence(annotations: Iterable<AnnotationDTO & { takeoff?: TakeoffLink | null }>): Map<string, number> {
  const groups = new Map<string, (AnnotationDTO & { takeoff?: TakeoffLink | null })[]>();
  for (const a of annotations) {
    if (a.deleted_at || !isMeasurement(a)) continue;
    const t = targetOf(a);
    if (!t) continue;
    const key = `${t.id}|${a.page_index}`;
    const g = groups.get(key);
    if (g) g.push(a);
    else groups.set(key, [a]);
  }
  const out = new Map<string, number>();
  for (const g of groups.values()) {
    g.sort((a, b) => {
      const ta = a.created_at ?? '￿', tb = b.created_at ?? '￿';
      if (ta !== tb) return ta < tb ? -1 : 1;
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
    g.forEach((a, i) => out.set(a.id, i + 1));
  }
  return out;
}

/** SVG path for a symbol centred at (cx, cy), radius r (screen space, y down). */
export function symbolPath(symbol: TakeoffSymbol, cx: number, cy: number, r: number): string {
  const ring = (n: number, start = -90, radius = r) =>
    Array.from({ length: n }, (_, i) => {
      const a = ((start + (i * 360) / n) * Math.PI) / 180;
      return [cx + radius * Math.cos(a), cy + radius * Math.sin(a)] as const;
    });
  let pts: (readonly [number, number])[];
  switch (symbol) {
    case 'square':
      pts = [[cx - r, cy - r], [cx + r, cy - r], [cx + r, cy + r], [cx - r, cy + r]];
      break;
    case 'triangle':
      pts = ring(3);
      break;
    case 'diamond':
      pts = ring(4);
      break;
    case 'hexagon':
      pts = ring(6, 0);
      break;
    case 'star': {
      const o = ring(5), i = ring(5, -54, r * 0.45);
      pts = o.flatMap((p, k) => [p, i[k]]);
      break;
    }
    default:
      return `M ${cx - r} ${cy} a ${r} ${r} 0 1 0 ${2 * r} 0 a ${r} ${r} 0 1 0 ${-2 * r} 0 Z`;
  }
  return `M ${pts.map(([x, y]) => `${x.toFixed(2)} ${y.toFixed(2)}`).join(' L ')} Z`;
}

/** Short label for a host assembly without a code ("Refurbished Pylon" → RP). */
export function abbreviation(name: string): string {
  return (name.replace(/-/g, ' ').split(/\s+/).filter(Boolean).slice(0, 4).map((w) => w[0]).join('') || 'H').toUpperCase();
}

// ---------------------------------------------------------------------------
// REST client
// ---------------------------------------------------------------------------

export class TakeoffConflict extends Error {
  constructor(readonly server: Record<string, unknown>) {
    super('Someone else changed this first — showing their version');
    this.name = 'TakeoffConflict';
  }
}

export type CatalogKind = 'items' | 'assemblies' | 'areas' | 'unit-types' | 'masks';

/** `base` defaults to a drawing set's takeoff; boards pass `/api/boards/<id>/takeoff/`. */
export function takeoffApi(api: ApiClient, documentId: string, baseOverride?: string) {
  const base = baseOverride ?? `/api/documents/${documentId}/takeoff/`;
  const body = (method: string, data: unknown): RequestInit => ({ method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
  const guard = async <T>(p: Promise<T>): Promise<T> => {
    try {
      return await p;
    } catch (e) {
      const err = e as { status?: number; body?: { server?: Record<string, unknown> } };
      if (err.status === 409 && err.body?.server) throw new TakeoffConflict(err.body.server);
      throw e;
    }
  };
  return {
    state: () => api.json<TakeoffStateDTO>(base),
    quantities: () => api.json<QuantitiesDTO>(`${base}quantities/`),
    setDefaultScope: (scope: string) => api.json<TakeoffStateDTO>(base, body('PATCH', { default_scope: scope })),
    setPricing: (pricing: Partial<PricingSettings>) => api.json<TakeoffStateDTO>(base, body('PATCH', { pricing })),
    create: <T>(kind: CatalogKind, data: Record<string, unknown>) => api.json<T>(`${base}${kind}/`, body('POST', data)),
    update: <T>(kind: CatalogKind, id: string, version: number, patch: Record<string, unknown>) =>
      guard(api.json<T>(`${base}${kind}/${id}/`, body('PATCH', { ...patch, version }))),
    remove: (kind: CatalogKind, id: string) => api.json<void>(`${base}${kind}/${id}/`, { method: 'DELETE' }),
    duplicateItem: (id: string) => api.json<TakeoffItemDTO>(`${base}items/${id}/duplicate/`, body('POST', {})),
    copyMask: (id: string, pages: number[] | 'all') => api.json<MaskDTO[]>(`${base}masks/${id}/copy/`, body('POST', { page_indices: pages })),
    setSheet: (pageIndex: number, data: Record<string, unknown>) => guard(api.json<TakeoffSheetDTO>(`${base}sheets/${pageIndex}/`, body('PUT', data))),
    placeCounts: (data: { item_id?: string; assembly_id?: string; page_index: number; points: P[]; allow_duplicates?: boolean }) =>
      api.json<{ created: AnnotationDTO[]; skipped: (P & { reason: string })[]; quantities: QuantitiesDTO }>(`${base}counts/`, body('POST', data)),
    removeMarks: (ids: string[]) => api.json<{ removed: string[]; quantities: QuantitiesDTO }>(`${base}counts/remove/`, body('POST', { annotation_ids: ids })),
    exportXlsx: async () => (await api.request(`${base}export.xlsx`)).blob(),
    exportCsv: async (table: 'tuples' | 'project_bom' | 'area_zone_bom' | 'budget') => (await api.request(`${base}export.csv?table=${table}`)).blob(),
    exportSheets: (title?: string) => api.json<{ id: string; name: string; url: string }>(`${base}export-sheets/`, body('POST', title ? { title } : {})),
  };
}
export type TakeoffApi = ReturnType<typeof takeoffApi>;
